import {Loader, Stack} from '@mantine/core'
import {useQuery} from '@tanstack/react-query'
import {createFileRoute} from '@tanstack/react-router'

import {getProspectsByGivingLevel} from 'src/api/client/api'
import {ManagerFilter} from 'src/components/analytics/manager-filter'
import ProspectsByGivingLevel, {
    ProspectsByGivingLevelExportContent,
} from 'src/components/analytics/prospects-by-giving-level'
import {useProspectNavigation} from 'src/components/analytics/use-prospect-navigation'
import {ExportImageButton} from 'src/components/export-image-button'
import {PageHeader} from 'src/components/page-header'
import {useScenarioPlanning} from 'src/components/scenario/scenario-context'

type SearchParams = {manager?: string}

export const Route = createFileRoute('/_authed/organizations/$orgId/prospects-by-giving-level')({
    component: ProspectsByGivingLevelPage,
    validateSearch: (search: Record<string, unknown>): SearchParams => ({
        manager: (search['manager'] as string) || undefined,
    }),
})

function ProspectsByGivingLevelPage() {
    const {orgId} = Route.useParams()
    const {manager} = Route.useSearch()
    const navigateToProspects = useProspectNavigation(orgId)
    const {appliedScenario} = useScenarioPlanning()

    const {data, isLoading, error} = useQuery({
        queryKey: ['page', orgId, 'prospects-by-giving-level', manager, appliedScenario],
        queryFn: () => getProspectsByGivingLevel(orgId, manager, appliedScenario),
    })

    if (isLoading) {
        return <Loader size="lg" style={{display: 'block', margin: '60px auto'}} />
    }

    if (error) {
        return <div>Error loading page: {(error as Error).message}</div>
    }

    if (!data) return null

    const headerTitle = 'Prospects by Giving Level'
    const headerDescription = 'Prospect counts and estimated giving ranges by capacity bucket.'

    return (
        <Stack>
            <PageHeader
                title={headerTitle}
                description={headerDescription}
                actions={
                    <ExportImageButton
                        title={headerTitle}
                        renderContent={({kind}) => (
                            <ProspectsByGivingLevelExportContent data={data} kind={kind} />
                        )}
                        filenameBase="prospects-by-giving-level"
                        exportWidthPx={{chart: 960, table: 680}}
                    />
                }
            />
            <ManagerFilter orgId={orgId} />
            <ProspectsByGivingLevel
                data={data}
                onProspectFilter={(filters) => navigateToProspects({...filters, manager})}
            />
        </Stack>
    )
}
